import { useState, useRef, useEffect } from 'react';
import { AlertCircle, Edit3 } from 'lucide-react';
import { useResume } from '../context/ResumeContext';
import { TEMPLATES, TOOL_FONTS_CSS } from '../constants/templates';
import { exportToPDF } from '../utils/exportPdf';
import { exportToWord } from '../utils/exportWord';
import Header from '../components/ui/Header';
import TemplatePicker from '../components/ui/TemplatePicker';
import AccentPicker from '../components/ui/AccentPicker';
import ExportBar from '../components/ui/ExportBar';
import ResumeForm from '../components/form/ResumeForm';
import TemplatePreview from '../components/resume/TemplatePreview';
import AdSlot from '../components/ui/AdSlot';

export default function EditorPage() {
  const {
    data,
    setData,
    selectedTemplate,
    setSelectedTemplate,
    activeCategory,
    setActiveCategory,
    parseError,
    accentOverrides,
    setAccent,
    setAccent2,
    resetAccent,
  } = useResume();

  const [isExporting, setIsExporting] = useState(null);
  const [previewScale, setPreviewScale] = useState(0.78);
  const [previewHeight, setPreviewHeight] = useState(11 * 96);
  const previewRef = useRef(null);
  const previewWrapRef = useRef(null);
  const printAreaRef = useRef(null);

  const template = TEMPLATES[selectedTemplate];
  const overrides = accentOverrides[selectedTemplate] || {};
  const effectiveAccent = overrides.accent || template.accent;
  const effectiveAccent2 = overrides.accent2 || template.accent2;
  const hasOverride = !!(overrides.accent || overrides.accent2);

  useEffect(() => {
    document.title = 'Resume Editor — Customize & Download Your Resume | Free Resume Builder';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      'Edit your resume with real-time preview. Choose from 12 professional templates, customize colors, and download as PDF or Word — free and ATS-friendly.'
    );
  }, []);

  useEffect(() => {
    const fit = () => {
      if (!previewWrapRef.current) return;
      const w = previewWrapRef.current.clientWidth;
      const targetW = 8.5 * 96;
      const padding = 32;
      const next = Math.min(1, (w - padding) / targetW);
      setPreviewScale(Math.max(0.35, next));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => {
    if (!previewRef.current) return;
    const el = previewRef.current;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect.height + 32;
        setPreviewHeight(h);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [selectedTemplate, data]);

  const handleExportPDF = () => {
    setIsExporting('pdf');
    try {
      exportToPDF(printAreaRef.current, data);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setTimeout(() => setIsExporting(null), 500);
    }
  };

  const handleExportDocx = () => {
    setIsExporting('docx');
    try {
      exportToWord(data, template, effectiveAccent, effectiveAccent2, data.name);
    } catch (err) {
      console.error('Word export failed:', err);
      alert('Word export failed.');
    } finally {
      setTimeout(() => setIsExporting(null), 400);
    }
  };

  return (
    <div className="min-h-screen w-full" style={{ background: '#F0EDE3' }}>
      <style>{TOOL_FONTS_CSS}</style>

      <Header />

      {parseError && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center gap-2 text-sm text-amber-900 tool-body no-print">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {parseError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] gap-0">
        {/* LEFT: Edit panel */}
        <aside className="bg-white border-r border-stone-200 lg:h-[calc(100vh-49px)] lg:overflow-y-auto preview-scroll no-print" aria-label="Resume editor form">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Edit3 className="w-4 h-4 text-stone-500" aria-hidden="true" />
              <h2 className="tool-display text-lg text-stone-900">Review &amp; Edit Your Resume</h2>
            </div>
            <p className="tool-body text-xs text-stone-500 mb-5 leading-relaxed">
              Fix any parsing mistakes before exporting. The preview updates as you type.
            </p>
            <ResumeForm data={data} setData={setData} />

            <div className="mt-6 pt-5 border-t border-stone-100">
              <AdSlot
                adSlot=""
                adFormat="rectangle"
                className="opacity-80"
                style={{ minHeight: '100px' }}
              />
            </div>
          </div>
        </aside>

        {/* RIGHT: Template picker + preview */}
        <main className="lg:h-[calc(100vh-49px)] flex flex-col overflow-hidden" aria-label="Resume preview and template selection">
          <div className="bg-white border-b border-stone-200 px-3 md:px-5 pt-3 no-print">
            <h2 className="sr-only">Choose a Resume Template</h2>
            <TemplatePicker
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              accentOverrides={accentOverrides}
            />
            <AccentPicker
              effectiveAccent={effectiveAccent}
              effectiveAccent2={effectiveAccent2}
              hasAccent2={!!template.accent2}
              hasOverride={hasOverride}
              setAccent={setAccent}
              setAccent2={setAccent2}
              resetAccent={resetAccent}
            />
          </div>

          {/* Preview area */}
          <div
            ref={previewWrapRef}
            className="flex-1 overflow-auto preview-scroll p-4 md:p-8 flex items-start justify-center"
            style={{ background: '#E8E4D8' }}
          >
            <div
              style={{
                width: `${8.5 * previewScale}in`,
                height: `${previewHeight * previewScale}px`,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  width: '8.5in',
                }}
              >
                <TemplatePreview
                  ref={previewRef}
                  template={template}
                  data={data}
                  accent={effectiveAccent}
                  accent2={effectiveAccent2}
                />
              </div>
            </div>
          </div>

          <ExportBar
            templateLabel={template.label}
            isExporting={isExporting}
            onExportPDF={handleExportPDF}
            onExportDocx={handleExportDocx}
          />
        </main>
      </div>

      {/* Off-screen full-size resume for PDF export */}
      <div ref={printAreaRef} className="pdf-print-area" aria-hidden="true">
        <TemplatePreview
          template={template}
          data={data}
          accent={effectiveAccent}
          accent2={effectiveAccent2}
        />
      </div>
    </div>
  );
}
